import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ExchangeRate {
  id: string;
  from_currency: string;
  to_currency: string;
  rate: number;
  effective_date: string;
  source: string | null;
  created_at: string | null;
}

export interface CreateExchangeRateData {
  from_currency: string;
  to_currency: string;
  rate: number;
  effective_date: string;
  source?: string;
}

export function useExchangeRates() {
  return useQuery({
    queryKey: ['exchange-rates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('exchange_rates')
        .select('*')
        .order('effective_date', { ascending: false });

      if (error) throw error;
      return data as ExchangeRate[];
    },
  });
}

export function useLatestExchangeRate(fromCurrency: string, toCurrency: string) {
  return useQuery({
    queryKey: ['exchange-rates', 'latest', fromCurrency, toCurrency],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('exchange_rates')
        .select('*')
        .eq('from_currency', fromCurrency)
        .eq('to_currency', toCurrency)
        .order('effective_date', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data as ExchangeRate | null;
    },
  });
}

export function useCreateExchangeRate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateExchangeRateData) => {
      const { data: result, error } = await supabase
        .from('exchange_rates')
        .insert(data)
        .select()
        .single();

      if (error) throw error;
      return result as ExchangeRate;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exchange-rates'] });
    },
  });
}

export function useUpdateExchangeRate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<ExchangeRate> & { id: string }) => {
      const { data: result, error } = await supabase
        .from('exchange_rates')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return result as ExchangeRate;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exchange-rates'] });
    },
  });
}

export function useDeleteExchangeRate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('exchange_rates')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exchange-rates'] });
    },
  });
}

export function convertCurrency(
  amount: number,
  fromCurrency: string,
  toCurrency: string,
  rate: number
): number {
  if (fromCurrency === toCurrency) return amount;
  return amount / rate;
}
